import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { unwrap, type ApiError } from "@/api/errors";
import { queryKeys } from "@/api/queryKeys";
import type { ProjectClientCreate, ProjectClientPatch, ProjectOut } from "@/api/types";

export type { ProjectOut } from "@/api/types";

async function fetchProjects(slug: string): Promise<ProjectOut[]> {
  return unwrap(await api.GET("/orgs/{slug}/projects", { params: { path: { slug } } }));
}

export function useProjects(orgSlug: string) {
  return useQuery<ProjectOut[], ApiError>({
    queryKey: queryKeys.projects(orgSlug),
    queryFn: () => fetchProjects(orgSlug),
  });
}

/** Projects for several organisations in parallel (Home). */
export function useProjectsForOrgs(orgSlugs: string[]) {
  return useQueries({
    queries: orgSlugs.map((slug) => ({
      queryKey: queryKeys.projects(slug),
      queryFn: () => fetchProjects(slug),
    })),
  });
}

export function useProject(orgSlug: string, projectSlug: string) {
  return useQuery<ProjectOut, ApiError>({
    queryKey: queryKeys.project(orgSlug, projectSlug),
    queryFn: async () =>
      unwrap(await api.GET("/orgs/{slug}/projects/{pslug}", { params: { path: { slug: orgSlug, pslug: projectSlug } } })),
  });
}

/** Owner asks for a new project; it starts as `requested` and Codemonk is emailed. */
export function useCreateProject(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation<ProjectOut, ApiError, ProjectClientCreate>({
    mutationFn: async (body) => unwrap(await api.POST("/orgs/{slug}/projects", { params: { path: { slug: orgSlug } }, body })),
    onSuccess: (p) => {
      qc.setQueryData(queryKeys.project(orgSlug, p.slug), p);
      void qc.invalidateQueries({ queryKey: queryKeys.projects(orgSlug), exact: true });
    },
  });
}

/** Owner edits description, goals, primary contact (and name while `requested`). */
export function usePatchProject(orgSlug: string, projectSlug: string) {
  const qc = useQueryClient();
  return useMutation<ProjectOut, ApiError, ProjectClientPatch>({
    mutationFn: async (body) =>
      unwrap(await api.PATCH("/orgs/{slug}/projects/{pslug}", { params: { path: { slug: orgSlug, pslug: projectSlug } }, body })),
    onSuccess: (p) => {
      qc.setQueryData(queryKeys.project(orgSlug, projectSlug), p);
      void qc.invalidateQueries({ queryKey: queryKeys.projects(orgSlug), exact: true });
      void qc.invalidateQueries({ queryKey: queryKeys.activity(orgSlug, projectSlug) });
    },
  });
}
